import React from 'react'
import { Pressable, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { interpolateColor, useAnimatedStyle } from 'react-native-reanimated'
import { router } from 'expo-router'

import useRWD from '@ui/theme/useRWD'
import { gap } from '@ui/theme/values'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import PricePackage from '@order/PricePackage'
import TileGlass from '@ui/tiles/TileGlass'
import { colors } from '@ui/colors'
import Gradient from '@ui/effects/Gradient'
import Typography from '@ui/typography'
import { type StepOption } from '@order/steps/types'

import Label from './Label'

export default function OptionVideo({
  // id
  // value,
  title,
  desc,
  label,
  image,
  price,
  pricePrefix,
  recommended,
  stepVariable,
  colorOption,
  large,
  onPress,
  ...props
}: StepOption) {
  const { screen } = useRWD()
  const { t } = useTranslation(['steps', 'ui-steps'])
  const { animation, onPressIn, onPressOut } = useAnimatedPress()
  const sizeContainer = (screen.width - 5 * gap) / 2
  const sizeImage = large ? 128 : 64
  const sizeBlur = large ? 256 : 128

  const onPressStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        animation.value,
        [0, 1],
        recommended
          ? [colors.white, colors.white25]
          : [colors.white0, colors.white75]
      ),
    }
  })

  return (
    <Pressable
      className="flex-1"
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
    >
      <View
        style={{
          width: sizeContainer,
          height: '100%',
        }}
      >
        <TileGlass
          image={image}
          className="h-full w-full"
          style={[
            {
              borderWidth: 1,
              borderColor: colors.white75,
            },
            onPressStyle,
          ]}
          imageStyle={{ width: sizeImage, height: sizeImage }}
          imageWrapperStyle={[
            large ? { bottom: 128, left: 96 } : { bottom: 24, left: 32 },
          ]}
          // blur={0}
          blurClassName={
            large
              ? 'bottom-0 left-0 right-0 top-0 items-center justify-center'
              : 'bottom-0 left-0 right-0 top-0 items-start justify-start'
          }
          blurStyle={[
            {
              opacity: 1,
              width: sizeBlur,
              height: sizeBlur,
              position: 'absolute',
            },
            large
              ? { transform: [{ translateY: 28 }] }
              : { bottom: 0, left: 0, transform: [{ translateY: 8 }] },
          ]}
          gradient={
            <Gradient
              colors={[colors.white75, colors.white0]}
              start={[0, 0]}
              end={[0.5, 1]}
            />
          }
        >
          <View
            style={{
              paddingHorizontal: 32,
              paddingTop: 32, // large ? 32 : 28,
              paddingBottom: 24,
            }}
          >
            <View style={{ gap: large ? 8 : 4 }}>
              <Typography
                variant="LGB"
                className="leading-tighter tracking-[-1]"
              >
                {title}
              </Typography>
              <Typography variant="SM" className="color-text/50 leading-tight">
                {desc}
              </Typography>
            </View>
          </View>
          <View
            className="absolute"
            style={
              large ? { bottom: 24, right: 24 } : { bottom: 16, right: 16 }
            }
          >
            <PricePackage
              price={price}
              prefix={pricePrefix}
              colorOption={colorOption}
            />
          </View>
        </TileGlass>
        {label && !recommended && (
          <Label text={label} colorOption={colorOption} />
        )}
        {recommended && (
          <Label
            text={t('ui-steps:option.label.recommended')}
            colorOption={colorOption}
          />
        )}
      </View>
    </Pressable>
  )
}
